import { useCallback, useEffect, useMemo, useState } from 'react';
import { Megaphone, Plus, Pin, Pencil, Trash2, BellRing, Check, ChevronLeft, Users, Paperclip } from 'lucide-react';
import ModulLayout from '../../layout/ModulLayout';
import ListToolbar, { PohledPill } from '../../layout/ListToolbar';
import { KDoreseniSeznam } from '../../layout/KDoreseni';
import ModalShell from '../../ui/ModalShell';
import usePotvrzeni from '../../ui/usePotvrzeni';
import Avatar from '../../../lib/avatar';
import { renderFormattedText } from '../../ChatWindow';
import { SECTION_COLOR, SECTION_BG, hlavicky, formatCasListu } from '../komunikace';
import OznameniEditor from './OznameniEditor';
import PrilohyMrizka from '../prilohy/PrilohyMrizka';
import ZpetTlacitko from '../../ui/ZpetTlacitko';

/**
 * Komunikace → Nástěnka (etapa 3). Norma §1: seznam oznámení · detail ·
 * třetí sloupec. Autor a admin vidí ve třetím sloupci potvrzení (kdo ano,
 * kdo ne, „Připomenout"); zaměstnanec v detailu „Přečetl jsem".
 * Na mobilu jde přehled potvrzení přes řádek pod hlavičkou do `ModalShell`.
 */
const POHLEDY = [
    { id: 'plati', label: 'Platná', patri: o => o.plati },
    { id: 'potvrdit', label: 'K potvrzení', upozorneni: true, patri: o => o.plati && o.vyzaduje_potvrzeni && o.ja && !o.ja.potvrzeno_at },
    { id: 'moje', label: 'Moje', jenAutor: true, patri: (o, uid) => o.autor_id === uid },
    { id: 'archiv', label: 'Archiv', patri: o => !o.plati },
];

const datum = (d) => (d ? new Date(d).toLocaleDateString('cs', { day: 'numeric', month: 'numeric', year: 'numeric' }) : '');

export default function Nastenka({ user, isDark, isMobile, cardStyle, inputStyle, mutedColor, initialOznameniId = null, rezervaDole = null, onZmenaPoctu }) {
    const [data, setData] = useState(null);
    const [pohled, setPohled] = useState('plati');
    const [hledani, setHledani] = useState('');
    const [vybranyId, setVybranyId] = useState(initialOznameniId);
    const [detail, setDetail] = useState(null);
    const [editor, setEditor] = useState(null);            // null | 'nove' | oznámení
    const [potvrzeniMobil, setPotvrzeniMobil] = useState(false);
    const [hlaska, setHlaska] = useState(null);
    const [potvrdit, potvrzeniDialog] = usePotvrzeni(isDark);

    useEffect(() => { if (initialOznameniId) setVybranyId(initialOznameniId); }, [initialOznameniId]);

    const nacti = useCallback(() => {
        fetch('/api/nastenka').then(r => (r.ok ? r.json() : null)).then(d => {
            if (!d) return;
            setData(d);
            onZmenaPoctu?.(d.oznameni.filter(o => o.plati && o.vyzaduje_potvrzeni && o.ja && !o.ja.potvrzeno_at).length);
        }).catch(() => {});
    }, [onZmenaPoctu]);
    useEffect(() => { nacti(); }, [nacti]);

    const nactiDetail = useCallback((id) => {
        if (!id) { setDetail(null); return; }
        fetch(`/api/nastenka/${id}`).then(r => (r.ok ? r.json() : null)).then(setDetail).catch(() => setDetail(null));
    }, []);
    useEffect(() => { nactiDetail(vybranyId); setHlaska(null); }, [vybranyId, nactiDetail]);

    const oznameni = data?.oznameni || [];
    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const pocty = useMemo(() => Object.fromEntries(POHLEDY.map(p => [p.id, oznameni.filter(o => p.patri(o, user?.id)).length])), [oznameni, user]);
    const pohledy = POHLEDY.filter(p => !p.jenAutor || data?.smi_psat);
    const hledat = hledani.trim().toLowerCase();
    const seznam = oznameni.filter(o => aktivniPohled.patri(o, user?.id) && (!hledat || (o.nazev + ' ' + o.text).toLowerCase().includes(hledat)));

    const potvrditPrecteni = async () => {
        const res = await fetch(`/api/nastenka/${vybranyId}/potvrdit`, { method: 'POST', headers: hlavicky(false) });
        if (res.ok) { nacti(); nactiDetail(vybranyId); window.dispatchEvent(new Event('nastenka-zmena')); }
    };
    const pripomenout = async () => {
        const res = await fetch(`/api/nastenka/${vybranyId}/pripomenout`, { method: 'POST', headers: hlavicky(false) });
        const d = await res.json().catch(() => ({}));
        setHlaska(res.ok ? (d.pripomenuto ? `Připomenuto ${d.pripomenuto} lidem` : 'Všem už bylo připomenuto v posledních 12 hodinách') : (d.error || 'Nepodařilo se'));
        if (res.ok) nactiDetail(vybranyId);
    };
    const smazat = async () => {
        if (!(await potvrdit('Smazat oznámení?', { text: 'Zmizí z Nástěnky všem příjemcům.', potvrdit: 'Smazat' }))) return;
        const res = await fetch(`/api/nastenka/${vybranyId}`, { method: 'DELETE', headers: hlavicky(false) });
        if (res.ok) { setVybranyId(null); nacti(); }
    };

    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';
    const muted = mutedColor;

    // ── Seznam ──
    const radek = (o, siroky) => {
        const aktivni = o.id === vybranyId;
        const kPotvrzeni = o.plati && o.vyzaduje_potvrzeni && o.ja && !o.ja.potvrzeno_at;
        const p = o.pocty;
        const podil = p && p.prijemcu ? p.potvrdilo / p.prijemcu : 0;
        return (
            <button key={o.id} type="button" onClick={() => setVybranyId(o.id)} className="w-full text-left flex items-center"
                style={{ gap: 12, padding: isMobile ? '12px 14px' : '10px 14px', minHeight: 64, background: aktivni ? SECTION_BG : 'transparent', boxShadow: aktivni ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        {o.pripnuto && <Pin size={12} style={{ color: SECTION_COLOR, flexShrink: 0 }} />}
                        <span className="truncate font-semibold" style={{ color: 'var(--text-primary)', fontSize: isMobile ? 15 : 13.5 }}>{o.nazev}</span>
                        {o.prilohy?.length > 0 && <Paperclip size={12} aria-label={`${o.prilohy.length} příloh`} style={{ color: muted, flexShrink: 0 }} />}
                    </div>
                    <p className="truncate" style={{ margin: '2px 0 0', fontSize: isMobile ? 13 : 12, color: muted }}>
                        {o.cil_popis} · {formatCasListu(o.publikovano_at)}{o.plati_do ? ` · do ${datum(o.plati_do)}` : ''}
                    </p>
                    {p && o.vyzaduje_potvrzeni && !siroky && (
                        <div className="flex items-center gap-2" style={{ marginTop: 6 }}>
                            <div style={{ width: 110, height: 4, borderRadius: 2, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                                <div style={{ width: `${Math.round(podil * 100)}%`, height: '100%', background: SECTION_COLOR }} />
                            </div>
                            <span className="text-[11px] tabular-nums" style={{ color: muted }}>{p.potvrdilo} z {p.prijemcu}</span>
                        </div>
                    )}
                </div>
                {siroky && <span className="shrink-0 text-xs" style={{ width: 150, color: muted }}>{o.autor}</span>}
                {siroky && (
                    <span className="shrink-0 text-xs tabular-nums" style={{ width: 110, color: muted }}>
                        {p && o.vyzaduje_potvrzeni ? `potvrdilo ${p.potvrdilo} z ${p.prijemcu}` : (o.vyzaduje_potvrzeni ? 's potvrzením' : 'k přečtení')}
                    </span>
                )}
                {kPotvrzeni && (
                    <span className="shrink-0 text-[11px] font-bold rounded-full" style={{ padding: '2px 8px', background: SECTION_COLOR, color: '#fff' }}>Potvrdit</span>
                )}
            </button>
        );
    };

    const seznamPanel = ({ siroky }) => (
        <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={isMobile} sklo isDark={isDark} mutedColor={muted} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{ value: hledani, onChange: setHledani, placeholder: 'Hledat v oznámeních…', placeholderKratky: 'Hledat' }}
                akce={data?.smi_psat ? [{ id: 'nove', label: 'Oznámení', Icon: Plus, primarni: true, onClick: () => setEditor('nove') }] : []}
                pohledy={!siroky && !isMobile ? pohledy.map(p => ({ id: p.id, label: p.label, pocet: pocty[p.id], upozorneni: p.upozorneni, barva: p.upozorneni ? SECTION_COLOR : undefined })) : null}
                pohledAktivni={aktivniPohled.id}
                onPohled={setPohled}
                pohled={!siroky && !isMobile ? null : pohledy.map(p => (
                    <PohledPill key={p.id} label={p.label} pocet={pocty[p.id]} aktivni={aktivniPohled.id === p.id}
                        upozorneni={p.upozorneni && pocty[p.id] > 0} onClick={() => setPohled(p.id)}
                        isDark={isDark} mutedColor={muted} barva={SECTION_COLOR} kompaktni={isMobile} />
                ))}
            />
            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {data === null && <p className="text-center text-sm" style={{ color: muted, padding: 32 }}>Načítám…</p>}
                {data && seznam.length === 0 && (
                    <p className="text-center text-sm" style={{ color: muted, padding: '32px 16px' }}>
                        {hledat ? 'Nic se neshoduje' : pohled === 'potvrdit' ? 'Všechno potvrzené.' : pohled === 'archiv' ? 'Archiv je prázdný.' : 'Zatím žádná oznámení.'}
                    </p>
                )}
                {seznam.map(o => radek(o, siroky))}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );

    // ── Potvrzení (třetí sloupec / mobilní modal) ──
    const potvrzeniObsah = (kompaktni) => {
        if (!detail?.prijemci) return null;
        const pr = detail.prijemci;
        const potvrdili = pr.filter(p => p.potvrzeno_at);
        const precetli = pr.filter(p => p.precteno_at && !p.potvrzeno_at);
        const neotevreli = pr.filter(p => !p.precteno_at);
        const nepotvrdili = [...precetli, ...neotevreli];
        const nadpis = { fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '16px 0 6px' };
        const radekCloveka = (p, popis) => (
            <div key={p.id} className="flex items-center" style={{ gap: 8, minHeight: kompaktni ? 44 : 34, borderBottom: `1px solid ${cara}` }}>
                <Avatar url={p.avatar_url} jmeno={p.jmeno} size={24} isDark={isDark} />
                <span className="flex-1 min-w-0 truncate text-[13px]" style={{ color: 'var(--text-primary)' }}>{p.jmeno}</span>
                <span className="text-[11px]" style={{ color: muted }}>{popis}</span>
            </div>
        );
        const polozky = detail.vyzaduje_potvrzeni && detail.plati && nepotvrdili.length
            ? [{ id: 'nepotvrdili', problem: !!detail.plati_do, titulek: `${nepotvrdili.length} lidí nepotvrdilo`, popis: detail.plati_do ? `platí do ${datum(detail.plati_do)}` : 'oznámení bez data platnosti' }]
            : [];
        return (
            <div style={kompaktni ? { padding: '4px 16px 16px' } : null}>
                {detail.vyzaduje_potvrzeni ? (
                    <>
                        <p style={{ ...nadpis, marginTop: kompaktni ? 12 : 0 }}>Potvrzení</p>
                        <p className="tabular-nums" style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px', lineHeight: 1.1 }}>{potvrdili.length} / {pr.length}</p>
                        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}><span style={{ color: muted }}>Přečetlo, nepotvrdilo</span><b style={{ color: 'var(--text-primary)' }}>{precetli.length}</b></div>
                        <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}><span style={{ color: muted }}>Neotevřelo</span><b style={{ color: 'var(--text-primary)' }}>{neotevreli.length}</b></div>
                    </>
                ) : (
                    <>
                        <p style={{ ...nadpis, marginTop: kompaktni ? 12 : 0 }}>Přečtení</p>
                        <p className="tabular-nums" style={{ fontSize: 28, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 4px', lineHeight: 1.1 }}>{pr.length - neotevreli.length} / {pr.length}</p>
                    </>
                )}
                {polozky.length > 0 && (
                    <>
                        <p style={nadpis}>K dořešení</p>
                        <KDoreseniSeznam polozky={polozky} isDark={isDark} />
                        <button type="button" onClick={pripomenout} className="w-full flex items-center justify-center gap-1.5 rounded-lg font-semibold"
                            style={{ height: kompaktni ? 44 : 34, marginTop: 8, fontSize: 13, background: 'transparent', color: 'var(--text-primary)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, cursor: 'pointer' }}>
                            <BellRing size={14} /> Připomenout {nepotvrdili.length} lidem
                        </button>
                        {hlaska && <p className="text-xs" style={{ color: muted, marginTop: 6 }}>{hlaska}</p>}
                    </>
                )}
                {detail.vyzaduje_potvrzeni && nepotvrdili.length > 0 && <p style={nadpis}>Nepotvrdili · {nepotvrdili.length}</p>}
                {detail.vyzaduje_potvrzeni && nepotvrdili.map(p => radekCloveka(p, p.precteno_at ? `přečetl ${formatCasListu(p.precteno_at)}` : 'neotevřel'))}
                {potvrdili.length > 0 && <p style={nadpis}>Potvrdili · {potvrdili.length}</p>}
                {potvrdili.map(p => radekCloveka(p, formatCasListu(p.potvrzeno_at)))}
                {!detail.vyzaduje_potvrzeni && neotevreli.length > 0 && <p style={nadpis}>Zatím neotevřeli · {neotevreli.length}</p>}
                {!detail.vyzaduje_potvrzeni && neotevreli.map(p => radekCloveka(p, ''))}
            </div>
        );
    };

    // ── Detail ──
    const detailPanel = () => {
        const d = detail && detail.id === vybranyId ? detail : null;
        const mamPotvrdit = d && d.vyzaduje_potvrzeni && d.ja && !d.ja.potvrzeno_at;
        const tlac = { height: 34, padding: '0 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, color: 'var(--text-primary)', display: 'inline-flex', alignItems: 'center', gap: 6 };
        return (
            <div className={isMobile ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'} style={isMobile ? null : cardStyle}>
                <div style={{ padding: isMobile ? '10px 14px 12px' : '16px 18px 12px', borderBottom: `1px solid ${cara}`, flexShrink: 0 }}>
                    {isMobile && (
                        <ZpetTlacitko onClick={() => setVybranyId(null)} popis="Zpět na nástěnku" sklo isDark={isDark} style={{ marginBottom: 10 }} />
                    )}
                    <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                            <h2 className="font-bold" style={{ fontSize: isMobile ? 18 : 17, color: 'var(--text-primary)', margin: 0 }}>
                                {d?.pripnuto && <Pin size={14} style={{ color: SECTION_COLOR, marginRight: 6, verticalAlign: -1 }} />}{d?.nazev || '…'}
                            </h2>
                            {d && (
                                <p className="text-xs" style={{ color: muted, margin: '4px 0 0' }}>
                                    {d.autor} · {datum(d.publikovano_at)} · {d.cil_popis}{d.plati_do ? ` · platí do ${datum(d.plati_do)}` : ''}{!d.plati ? ' · neplatné' : ''}
                                </p>
                            )}
                        </div>
                        {d?.smi_spravovat && !isMobile && (
                            <>
                                <button type="button" style={tlac} onClick={() => setEditor(d)}><Pencil size={13} /> Upravit</button>
                                <button type="button" style={{ ...tlac, color: '#EF4444' }} onClick={smazat} aria-label="Smazat"><Trash2 size={13} /></button>
                            </>
                        )}
                    </div>
                    {d?.smi_spravovat && isMobile && d.prijemci && (
                        <button type="button" onClick={() => setPotvrzeniMobil(true)} className="flex items-center w-full text-left"
                            style={{ marginTop: 10, gap: 10, height: 48, padding: '0 12px', borderRadius: 10, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.10)' : 'var(--border-default)'}`, color: 'var(--text-primary)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                            <Users size={17} style={{ color: 'var(--text-secondary)' }} />
                            <span className="flex-1">{d.vyzaduje_potvrzeni ? 'Potvrzení' : 'Přečtení'}</span>
                            <span className="tabular-nums" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                                {d.prijemci.filter(p => (d.vyzaduje_potvrzeni ? p.potvrzeno_at : p.precteno_at)).length} / {d.prijemci.length}
                            </span>
                        </button>
                    )}
                </div>
                <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0, padding: isMobile ? '14px 16px' : '16px 18px' }}>
                    {d ? (
                        <>
                            <div className="text-sm" style={{ color: 'var(--text-primary)', lineHeight: 1.6, maxWidth: 680 }}>{renderFormattedText(d.text)}</div>
                            {d.prilohy?.length > 0 && (
                                <div style={{ marginTop: 16, maxWidth: 680 }}>
                                    <PrilohyMrizka prilohy={d.prilohy} isDark={isDark} velikostNahledu={isMobile ? 96 : 110} />
                                </div>
                            )}
                        </>
                    ) : <p className="text-sm" style={{ color: muted }}>Načítám…</p>}
                    {mamPotvrdit && (
                        <button type="button" onClick={potvrditPrecteni} className="flex items-center justify-center gap-2 rounded-xl font-bold"
                            style={{ marginTop: 20, height: 48, width: isMobile ? '100%' : 260, background: SECTION_COLOR, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 15 }}>
                            <Check size={18} /> Přečetl jsem
                        </button>
                    )}
                    {d?.ja?.potvrzeno_at && (
                        <p className="flex items-center gap-1.5 text-xs" style={{ color: muted, marginTop: 16 }}>
                            <Check size={13} style={{ color: SECTION_COLOR }} /> Potvrzeno {formatCasListu(d.ja.potvrzeno_at)}
                        </p>
                    )}
                    {d?.smi_spravovat && isMobile && (
                        <div className="flex gap-2" style={{ marginTop: 20 }}>
                            <button type="button" style={{ ...tlac, height: 44 }} onClick={() => setEditor(d)}><Pencil size={14} /> Upravit</button>
                            <button type="button" style={{ ...tlac, height: 44, color: '#EF4444' }} onClick={smazat}><Trash2 size={14} /> Smazat</button>
                        </div>
                    )}
                    {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
                </div>
            </div>
        );
    };

    // Třetí sloupec: potvrzení (autor/admin), jinak souhrn Nástěnky
    const panel = () => {
        if (vybranyId && detail?.prijemci) {
            return <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>{potvrzeniObsah(false)}</div>;
        }
        const kPotvrzeni = pocty.potvrdit || 0;
        return (
            <div className="rounded-xl" style={{ ...cardStyle, padding: 16 }}>
                <p style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: muted, margin: '0 0 8px' }}>Nástěnka</p>
                <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}><span style={{ color: muted }}>Platných oznámení</span><b style={{ color: 'var(--text-primary)' }}>{pocty.plati || 0}</b></div>
                <div className="flex justify-between text-[13px]" style={{ minHeight: 30, alignItems: 'center', borderBottom: `1px solid ${cara}` }}><span style={{ color: muted }}>Čeká na mé potvrzení</span><b style={{ color: kPotvrzeni ? SECTION_COLOR : 'var(--text-primary)' }}>{kPotvrzeni}</b></div>
                {data?.smi_psat && (
                    <p className="text-xs" style={{ color: muted, marginTop: 12 }}>
                        Oznámení vidí jen ti, komu jsou určená. U oznámení s potvrzením tady uvidíte, kdo ho ještě nepotvrdil.
                    </p>
                )}
            </div>
        );
    };

    return (
        <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <ModulLayout
                isMobile={isMobile}
                vybrano={!!vybranyId}
                mobilRezim
                mobilPanel={false}
                panelVzdy
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={seznamPanel}
                detail={detailPanel}
                panel={panel}
            />
            {potvrzeniMobil && detail?.prijemci && (
                <ModalShell onClose={() => setPotvrzeniMobil(false)} isDark={isDark} nadpis={detail.nazev} ikona={Megaphone} barva={SECTION_COLOR}>
                    {potvrzeniObsah(true)}
                </ModalShell>
            )}
            {editor && (
                <OznameniEditor isDark={isDark} pozice={data?.pozice || []} puvodni={editor === 'nove' ? null : editor}
                    onClose={() => setEditor(null)}
                    onUlozeno={(o) => { setEditor(null); nacti(); setVybranyId(o.id); nactiDetail(o.id); }} />
            )}
            {potvrzeniDialog}
        </div>
    );
}
